import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  canSave$,
  formControlChanges$,
  NgDialogModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  IAccClaim,
  IAccClaimFundingRequest,
  IAccClaimPatientDetails,
  IAccClaimProviderDetails,
  IAccInvoiceBuilderClaimData,
  IAccInvoicePatientDetails,
  IAccInvoiceProviderDetails,
  IPatient,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  CASUAL_DATE_FORMAT,
  isWithRef,
  WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccClaimDetailsDisplayComponent } from '../acc-claim-details-display/acc-claim-details-display.component';
import { AccTestChipComponent } from '../acc-test-chip/acc-test-chip.component';
import { AccExistingClaimFormComponent } from './acc-existing-claim-form/acc-existing-claim-form.component';
import { AccExistingClaimFormGroup } from './acc-existing-claim-form/acc-existing-claim.formgroup';

export interface IAccClaimDialogResult {
  claimData: IAccInvoiceBuilderClaimData;
  claimRequest?: WithRef<IAccClaimFundingRequest>;
}

export interface IAccClaimDialogData {
  accClaims: WithRef<IAccClaimFundingRequest>[];
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  staffer?: WithRef<IStaffer>;
}

@Component({
  selector: 'pr-acc-claim-dialog',
  imports: [
    NgDialogModule,
    MatListModule,
    InformationBoxComponent,
    AccClaimDetailsDisplayComponent,
    AccExistingClaimFormComponent,
    AccTestChipComponent,
    MatDivider,
  ],
  templateUrl: './acc-claim-dialog.component.html',
  styleUrl: './acc-claim-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccClaimDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<IAccClaimDialogData, IAccClaimDialogResult>>(
      MatDialogRef
    );
  data = inject<IAccClaimDialogData>(MAT_DIALOG_DATA);
  selectedClaimData$: Observable<IAccClaim | undefined>;
  disabled$: Observable<boolean>;

  dateFormat = CASUAL_DATE_FORMAT;
  existingClaimOption = 'existingClaim';

  claimRequestCtrl = new TypedFormControl<
    WithRef<IAccClaimFundingRequest> | string
  >(first(this.data.accClaims) ?? this.existingClaimOption);

  purchaseOrderNumberCtrl = new TypedFormControl<string>('');

  existingClaimForm = new AccExistingClaimFormGroup(
    this.data.patient,
    this.data.practice,
    this.data.staffer
  );

  constructor() {
    this.selectedClaimData$ = formControlChanges$(this.claimRequestCtrl).pipe(
      map((claimRequest) =>
        isWithRef(claimRequest) ? claimRequest?.data.claimData : undefined
      )
    );

    this.disabled$ = combineLatest([
      formControlChanges$(this.claimRequestCtrl),
      canSave$(this.existingClaimForm),
    ]).pipe(
      map(([claimRequest, canSaveExistingClaimForm]) =>
        isWithRef(claimRequest) ? false : !canSaveExistingClaimForm
      )
    );
  }

  submit(): void {
    const claimRequest = isWithRef(this.claimRequestCtrl.value)
      ? this.claimRequestCtrl.value
      : undefined;
    const claimData = claimRequest
      ? this._claimRequestToClaimData(claimRequest)
      : this.existingClaimForm.getRawValue();

    this._dialogRef.close({
      claimData: {
        ...claimData,
        purchaseOrderNumber: this.purchaseOrderNumberCtrl.value,
      },
      claimRequest,
    });
  }

  getClaimSelectTitle(
    value?: string | WithRef<IAccClaimFundingRequest>
  ): string {
    if (isWithRef(value)) {
      return value.data.claimData.claimNumber ?? '';
    }
    if (value === this.existingClaimOption) {
      return 'Use Existing Claim';
    }
    return 'Select a Claim';
  }

  private _claimRequestToClaimData(
    claimRequest: WithRef<IAccClaimFundingRequest>
  ): IAccInvoiceBuilderClaimData {
    const claimData = claimRequest.data.claimData;
    return {
      claimNumber: claimData.claimNumber ?? '',
      accidentDate: claimData.injury?.accidentDate,
      patientDetails: toInvoicePatientDetails(claimData.patient?.details),
      providerDetails: toInvoiceProviderDetails(claimData.provider?.details),
    };
  }
}

function toInvoicePatientDetails(
  patient?: IAccClaimPatientDetails
): IAccInvoicePatientDetails | undefined {
  if (!patient) {
    return undefined;
  }
  return {
    nhi: patient.nhi,
    dateOfBirth: patient.dateOfBirth,
    firstName: patient.firstName,
    middleName: patient.middleName,
    surname: patient.surname,
  };
}

function toInvoiceProviderDetails(
  provider?: IAccClaimProviderDetails
): IAccInvoiceProviderDetails | undefined {
  if (!provider) {
    return undefined;
  }
  return {
    providerId: provider.providerId,
    firstName: provider.firstName,
    middleName: provider.middleName,
    surname: provider.surname,
  };
}
